import { ProjectRenderer } from '../ProjectRenderer';
import { Object3D, Vector3 } from 'three';
import { CreatorAccessory } from '@/types/creator';
import { AccessoryMountingSide } from '@/api/ProductTypeVariationApi';
import { CollidableObject } from './CollidableObject';
import { SerializedAccessory, UserDataProp } from '../types';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export class Accessory extends CollidableObject {
	private _mountingSide: CreatorAccessory['mountingSide'] =
		AccessoryMountingSide.BACK;
	private creatorAccessory: CreatorAccessory;
	hostSlot: Object3D | null = null;

	static readonly MOUTING_Z_OFFSET =
		RENDERER_CONFIG.ACCESSORIES.MOUTING_Z_OFFSET;

	constructor(
		renderer: ProjectRenderer,
		creatorAccessory: CreatorAccessory,
		object: Object3D
	) {
		super(
			renderer,
			{
				id: creatorAccessory.refId,
				posY: creatorAccessory.posY,
				linkedObjectId: creatorAccessory.linkedMiddlePostId,
			},
			object
		);

		this.renderer.project.add(object);

		this.creatorAccessory = creatorAccessory;
		this.mountingSide = creatorAccessory.mountingSide;

		if (this.creatorAccessory.variationAccessory.accessory.isSlotHosted) {
			this.renderer.project.traverse((child) => {
				if (
					child.userData[UserDataProp.SLOT_NAME] !==
					creatorAccessory.variationAccessory.accessory.hostSlotName
				)
					return;

				const slotWorldPos = new Vector3();
				child.getWorldPosition(slotWorldPos);

				this.object.position.copy(slotWorldPos);
				this.hostSlot = child;
			});
		}
	}

	get mountingSide() {
		return this._mountingSide;
	}

	set mountingSide(value: CreatorAccessory['mountingSide']) {
		this._mountingSide = value;
		let posZ: number;

		switch (value) {
			case AccessoryMountingSide.FRONT: {
				this.object.rotation.set(0, 0, 0);
				posZ =
					this.renderer.handleManager.objectsAvailableSpaceBox3.max.z -
					this.dimensions.z / 2 +
					Accessory.MOUTING_Z_OFFSET;
				break;
			}
			case AccessoryMountingSide.BACK: {
				this.object.rotation.set(0, Math.PI, 0);
				posZ =
					this.renderer.handleManager.objectsAvailableSpaceBox3.min.z +
					this.dimensions.z / 2 -
					Accessory.MOUTING_Z_OFFSET;
				break;
			}
		}

		if (!this.hostSlot) this.object.position.setZ(posZ);

		this.updateBox3();
	}

	override set posY(value: number) {
		this._posY = value;
		this.object.position.setY(value);
		this.updateBox3();
	}

	override get posY() {
		return this._posY;
	}

	override updateBox3() {
		this.object.updateMatrixWorld(true);
		this.collisionBox.setFromObject(this.object, true);

		const settings = this.creatorAccessory.variationAccessory.settings;

		this.collisionBox.set(
			new Vector3(
				this.collisionBox.min.x,
				this.collisionBox.min.y - settings.safeDistanceBottom / 1000,
				this.collisionBox.min.z - settings.safeDistanceDepth / 1000
			),
			new Vector3(
				this.collisionBox.max.x,
				this.collisionBox.max.y + settings.safeDistanceTop / 1000,
				this.collisionBox.max.z + settings.safeDistanceDepth / 1000
			)
		);
	}

	export(): SerializedAccessory {
		return {
			id: this.id,
			posY: this.posY,
			mountingSide: this.mountingSide,
		};
	}
}
